import React, { useState } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import AutoModeIcon from '@mui/icons-material/AutoMode';

import { AgentInvocationCard } from './AgentInvocationCard';

interface Props {
  agentIds: string[];
  defaultPromptAgentIds: string[];
  /** Optional seed text the invocation card pre-fills when the user opens
   *  an agent. Typically the active operation's prompt field. */
  defaultInstruction?: string;
}

/**
 * Studio-driven agent panel. Bucket 3c makes each featured-agent card
 * clickable: opening an `AgentInvocationCard` inline that runs the agent
 * via `useExecuteAgent` and surfaces the result with a "Use as prompt"
 * button that pushes the text into the active operation's prompt field
 * via `agentResultStore`.
 *
 * Single-slot expand: clicking a different card replaces the open one.
 * Clicking the same card again closes it.
 */
export const AgentPanel: React.FC<Props> = ({ agentIds, defaultPromptAgentIds, defaultInstruction }) => {
  const [openId, setOpenId] = useState<string | null>(null);
  const toggle = (id: string) => setOpenId(prev => (prev === id ? null : id));
  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        p: 2,
        overflowY: 'auto',
      }}
      aria-label="Agent panel"
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <SmartToyOutlinedIcon fontSize="small" />
          <Typography variant="overline" color="text.secondary">
            Agents
          </Typography>
        </Stack>

        {agentIds.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            This Studio doesn't feature any agents.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {agentIds.map(id =>
              openId === id ? (
                <AgentInvocationCard
                  key={id}
                  agentId={id}
                  defaultInstruction={defaultInstruction}
                  onClose={() => setOpenId(null)}
                />
              ) : (
                <Card key={id} variant="outlined">
                  <CardActionArea onClick={() => toggle(id)} aria-label={`Open agent ${humanize(id)}`}>
                    <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
                      <Typography variant="body2" fontWeight={600}>
                        {humanize(id)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {id}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              ),
            )}
          </Stack>
        )}

        {defaultPromptAgentIds.length > 0 && (
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <AutoModeIcon fontSize="inherit" sx={{ opacity: 0.7 }} />
              <Typography variant="caption" color="text.secondary">
                Auto-applied on every prompt
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {defaultPromptAgentIds.map(id => (
                <Chip
                  key={id}
                  label={humanize(id)}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: 11 }}
                />
              ))}
            </Stack>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

/**
 * Convert a kebab-case agent id ("fashion-design-agent") into a humanized
 * display name ("Fashion Design Agent"). Trailing "-agent" is stripped
 * since "Agent" is implied by the panel context.
 */
function humanize(id: string): string {
  const stripped = id.replace(/-agent$/i, '');
  return stripped
    .split('-')
    .filter(Boolean)
    .map(s => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');
}
